import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, FormHelperText } from "@mui/material";
import { Controller, Control, FieldValues, Path, FieldError } from "react-hook-form";

type OptionType<T> = { label: string; value: T; disabled?: boolean };

interface InputSelectProps<K extends FieldValues, T extends string | number> {
  name: Path<K>;
  control: Control<K>;
  error?: FieldError;
  options: OptionType<T>[];
  label?: string | React.ReactNode;
  disabled?: boolean;
  readonly?: boolean;
  clearable?: boolean;
  onChange?: (selectedValue: T | null) => void;
}

export default function InputSelect<K extends FieldValues, T extends string | number>({
  name,
  control,
  error,
  options,
  label,
  disabled,
  readonly,
  onChange,
  clearable = true
}: InputSelectProps<K, T>) {
  const EMPTY_VALUE = "__EMPTY__";

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FormControl fullWidth margin="dense" variant="standard" error={!!error} disabled={disabled || readonly}>
          <InputLabel>{label}</InputLabel>
          <Select
            value={field.value ?? EMPTY_VALUE}
            onChange={(event: SelectChangeEvent<T | typeof EMPTY_VALUE>) => {
              const newValue = event.target.value === EMPTY_VALUE ? null : event.target.value as T;
              field.onChange(newValue);
              onChange?.(newValue);
            }}
            readOnly={readonly}
            error={!!error}
          >
            { clearable &&
              <MenuItem value={EMPTY_VALUE}>
                Seleccione
              </MenuItem>
            }
            {options.map((option) => (
              <MenuItem key={String(option.value)} value={option.value} disabled={option.disabled}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          {error && <FormHelperText>{error.message}</FormHelperText>}
        </FormControl>
      )}
    />
  );
}
